import {
  ConfigApi,
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import {
  UptimeMetricRequest,
  UptimeMetricResponse,
  MonitorChecksHistoryResponse,
  UptimeMonitorRequest,
  UptimeMonitorResponse,
  Issue,
  IssuesRequest,
  IssuesListResponse,
  IssueDetailRequest,
  IssueEventLatestRequest,
  IssueEventDetails,
  IssueUpdateRequest,
  IssueUpdateResponse,
  StatusUpdateWithCommentRequest,
  IssueCommentsRequest,
  Comment,
  AddCommentRequest,
  UpdateCommentRequest,
  DeleteCommentRequest,
  TransactionGroup,
  TransactionGroupDetailRequest,
  TransactionGroupsListResponse,
  TransactionGroupsRequest,
  AISuggestionRequest,
  AISuggestionResponse,
  AIPredictionResponse,
  GetAIPredictionRequest,
  GenerateAISuggestionRequest,
} from '../types';
import { UptimeMonitorAPI } from './UptimeMonitorApi';
import { RequestMode } from '@internal/backstage-plugin-uptime-monitor-backend/src/enum';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { queryParamBuilder } from '../utils/queryParam';

type Options = {
  discoveryApi: DiscoveryApi;
  configApi: ConfigApi;
  fetchApi: FetchApi;
  identityApi: IdentityApi;
};

export class UptimeMonitorAPIImpl implements UptimeMonitorAPI {
  private readonly backendUrl: string | undefined;
  private readonly fetchApi: FetchApi;
  private readonly identityApi: IdentityApi;

  constructor(options: Options) {
    this.backendUrl = options.configApi.getOptionalString('backend.baseUrl');
    this.fetchApi = options.fetchApi;
    this.identityApi = options.identityApi;
  }

  private async getAuthToken() {
    const token = await this.identityApi.getCredentials();
    return token?.token;
  }

  private async getUrls() {
    return {
      apiUrl: `${this.backendUrl}/api/uptime-monitor/proxy/`,
    };
  }

  async getHealthCheck(
    params: UptimeMonitorRequest,
  ): Promise<UptimeMonitorResponse> {
    const { apiUrl } = await this.getUrls();
    const { org, monitorId, host } = params;

    const response = await this.fetchApi.fetch(
      `${apiUrl}${host}/${org}/monitors/${monitorId}`,
      {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status}`);
    }

    return await response.json();
  }

  async triggerMonitorCheck(
    params: UptimeMonitorRequest,
  ): Promise<UptimeMonitorResponse> {
    const { apiUrl } = await this.getUrls();
    const { org, monitorId, host } = params;

    const response = await this.fetchApi.fetch(
      `${apiUrl}${host}/${org}/monitors/${monitorId}/trigger`,
      {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to trigger monitor check: ${response.status}`);
    }

    return await response.json();
  }

  async getPaginatedMonitorChecksHistory(
    params: UptimeMonitorRequest,
  ): Promise<MonitorChecksHistoryResponse> {
    const { apiUrl } = await this.getUrls();
    const { org, monitorId, host, query } = params;

    let url = `${apiUrl}${host}/${org}/monitors/${monitorId}/checks`;
    url = queryParamBuilder(url, query);

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status}`);
    }

    return await response.json();
  }

  subscribeToHealthCheckUpdates(
    params: UptimeMonitorRequest,
    onUpdate: (data: UptimeMonitorResponse) => void,
    onError?: (error: Error) => void,
  ): Promise<() => void> {
    return new Promise(async (resolve, _reject) => {
      let currentEventSource: EventSourcePolyfill | null = null;
      let isClosedIntentionally = false;
      let retryCount = 0;
      const maxRetries = 5;
      const retryDelay = 3000;
      let tokenRefreshTimer: NodeJS.Timeout | null = null;

      const createConnection = async () => {
        try {
          if (currentEventSource) {
            currentEventSource.close();
          }

          const url = `${this.backendUrl}/api/uptime-monitor/proxy/${params.host}/${params.org}/monitors/${params.monitorId}?mode=${RequestMode.SSE}`;
          const token = await this.getAuthToken();

          currentEventSource = new EventSourcePolyfill(url, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            heartbeatTimeout: 3600000,
          });

          currentEventSource.onopen = () => {
            console.log('🚀 SSE connection opened');
            retryCount = 0;

            if (tokenRefreshTimer) {
              clearTimeout(tokenRefreshTimer);
            }
            tokenRefreshTimer = setTimeout(async () => {
              console.log('🚀 Token refresh timer triggered, reconnecting with fresh token');
              await createConnection();
            }, 55 * 60 * 1000);
          };

          currentEventSource.onmessage = event => {
            try {
              const message = JSON.parse(event.data);
              onUpdate(message.data);
            } catch (err) {
              console.error('Failed to parse SSE message', err);
              onError?.(err as Error);
            }
          };

          currentEventSource.onerror = async e => {
            console.error('SSE connection error', e);

            if (tokenRefreshTimer) {
              clearTimeout(tokenRefreshTimer);
              tokenRefreshTimer = null;
            }

            if (currentEventSource) {
              currentEventSource.close();
            }

            if (!isClosedIntentionally && retryCount < maxRetries) {
              const delay = retryDelay * Math.pow(2, retryCount);
              retryCount++;
              console.log(`🚀 Attempting to reconnect in ${delay}ms (attempt ${retryCount} of ${maxRetries})`);

              setTimeout(async () => {
                await createConnection();
              }, delay);
            } else if (retryCount >= maxRetries) {
              console.error(`Failed to connect after ${maxRetries} attempts`);
              onError?.(new Error(`Failed to connect after ${maxRetries} attempts`));
            } else {
              onError?.(new Error('SSE connection error'));
            }
          };
        } catch (connectionError) {
          console.error('Error establishing SSE connection', connectionError);
          onError?.(connectionError as Error);
        }
      };

      await createConnection();

      resolve(() => {
        isClosedIntentionally = true;
        if (tokenRefreshTimer) {
          clearTimeout(tokenRefreshTimer);
        }
        if (currentEventSource) {
          currentEventSource.close();
        }
        console.log('SSE connection closed intentionally');
      });
    });
  }

  async getUptimeMetrics(
    params: UptimeMetricRequest,
  ): Promise<UptimeMetricResponse> {
    const { apiUrl } = await this.getUrls();
    const { org, monitorId, host, query } = params;

    let url = `${apiUrl}${host}/${org}/monitors/${monitorId}/uptime`
    url = queryParamBuilder(url, query);

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status}`);
    }

    return await response.json();
  }

  async getUptimeMonitor(
    params: UptimeMetricRequest,
  ): Promise<UptimeMonitorResponse> {
    const { apiUrl } = await this.getUrls();
    const { org, monitorId, host, query } = params;

    let url = `${apiUrl}${host}/${org}/monitors/${monitorId}/checks`
    url = queryParamBuilder(url, query);

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status}`);
    }
    
    return await response.json();
  }

  async getIssues(params: IssuesRequest): Promise<IssuesListResponse> {
    const { apiUrl } = await this.getUrls();
    const { host, org, query } = params;

    let url = `${apiUrl}${host}/${org}/issues`;
    url = queryParamBuilder(url, query);

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch issues: ${response.status}`);
    }

    return await response.json();
  }

  async getIssueDetail(params: IssueDetailRequest): Promise<Issue> {
    console.log('API getIssueDetail called with:', params);

    const { apiUrl } = await this.getUrls();
    const { host, id } = params;

    const response = await this.fetchApi.fetch(
      `${apiUrl}${host}/issues/${id}`,
      {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      },
    );

    console.log('API getIssueDetail response:', response);

    if (!response.ok) {
      throw new Error(`Failed to fetch issue detail: ${response.status}`);
    }

    return await response.json();
  }

  async getIssueEventLatest(params: IssueEventLatestRequest): Promise<IssueEventDetails> {
    console.log('API getIssueEventLatest called with:', params);

    const { apiUrl } = await this.getUrls();
    const { host, id } = params;

    const response = await this.fetchApi.fetch(
      `${apiUrl}${host}/issues/${id}/events/latest`,
      {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      },
    );

    console.log('API getIssueEventLatest response:', response);

    if (!response.ok) {
      throw new Error(`Failed to fetch issue event details: ${response.status}`);
    }

    return await response.json();
  }

  async updateIssueStatus(params: IssueUpdateRequest): Promise<IssueUpdateResponse> {
    const { apiUrl } = await this.getUrls();
    const { host, org, status, id, ids } = params;

    const response = await this.fetchApi.fetch(
      `${apiUrl}${host}/${org}/issues/status`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({
          status,
          ...(id && { id }),
          ...(ids && { ids }),
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to update issue status: ${response.status}`);
    }

    return await response.json();
  }

  async updateIssueStatusWithComment(params: StatusUpdateWithCommentRequest): Promise<IssueUpdateResponse> {
    console.log('API updateIssueStatusWithComment called with:', params);

    const { host, org, issueId, status, comment } = params;
    const statusMessage = `Status changed to ${status.toUpperCase()}: ${comment}`;
    
    await this.addIssueComment({
      host,
      id: issueId.toString(),
      text: statusMessage,
    });

    return await this.updateIssueStatus({
      host,
      org,
      status,
      id: issueId,
    });
  }

  async getIssueComments(params: IssueCommentsRequest): Promise<Comment[]> {
    console.log('API getIssueComments called with:', params);

    const { apiUrl } = await this.getUrls();
    const { host, id } = params;

    const response = await this.fetchApi.fetch(
      `${apiUrl}${host}/issues/${id}/comments`,
      {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      },
    );

    console.log('API getIssueComments response:', response);

    if (!response.ok) {
      throw new Error(`Failed to fetch issue comments: ${response.status}`);
    }

    return await response.json();
  }

  async addIssueComment(params: AddCommentRequest): Promise<Comment> {
    console.log('API addIssueComment called with:', params);

    const { apiUrl } = await this.getUrls();
    const { host, id, text } = params;

    const response = await this.fetchApi.fetch(
      `${apiUrl}${host}/issues/${id}/comments`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({ text }),
      },
    );

    console.log('API addIssueComment response:', response);

    if (!response.ok) {
      throw new Error(`Failed to add issue comment: ${response.status}`);
    }

    return await response.json();
  }

  async updateIssueComment(params: UpdateCommentRequest): Promise<Comment> {
    console.log('API updateIssueComment called with:', params);

    const { apiUrl } = await this.getUrls();
    const { host, id, commentId, text } = params;

    const response = await this.fetchApi.fetch(
      `${apiUrl}${host}/issues/${id}/comments/${commentId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({ text }),
      },
    );

    console.log('API updateIssueComment response:', response);

    if (!response.ok) {
      throw new Error(`Failed to update issue comment: ${response.status}`);
    }

    return await response.json();
  }

  async deleteIssueComment(params: DeleteCommentRequest): Promise<void> {
    console.log('API deleteIssueComment called with:', params);

    const { apiUrl } = await this.getUrls();
    const { host, id, commentId } = params;

    const response = await this.fetchApi.fetch(
      `${apiUrl}${host}/issues/${id}/comments/${commentId}`,
      {
        method: 'DELETE',
        headers: {
          accept: 'application/json',
        },
      },
    );

    console.log('API deleteIssueComment response:', response);

    if (!response.ok) {
      throw new Error(`Failed to delete issue comment: ${response.status}`);
    }
  }

  async getTransactionGroups(
    params: TransactionGroupsRequest,
  ): Promise<TransactionGroupsListResponse> {
    const { apiUrl } = await this.getUrls();
    const { org, host, query } = params;

    let url = `${apiUrl}${host}/${org}/transaction-groups`;
    url = queryParamBuilder(url, query);

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch transaction groups: ${response.status}`);
    }

    return await response.json();
  }

  async getTransactionGroupDetail(
    params: TransactionGroupDetailRequest,
  ): Promise<TransactionGroup> {
    const { apiUrl } = await this.getUrls();
    const { host, org, id } = params;

    const response = await this.fetchApi.fetch(
      `${apiUrl}${host}/${org}/transaction-groups/${id}`,
      {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch transaction group detail: ${response.status}`);
    }

    return await response.json();
  }

  private async getAIUrls() {
    return {
      aiUrl: `${this.backendUrl}/api/ai-adapter/proxy/`,
    };
  }

  async getAIPrediction(params: GetAIPredictionRequest): Promise<AIPredictionResponse | null> {
    const { aiUrl } = await this.getAIUrls();
    const { data_id } = params;

    try {
      const response = await this.fetchApi.fetch(
        `${aiUrl}v1/core/predictions/${data_id}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
          },
        },
      );

      if (response.status === 404) {
        // No prediction exists for this data_id
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch AI prediction: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      // If it's a 404 or network error for non-existent prediction, return null
      if (error instanceof Error && (error.message.includes('404') || error.message.includes('Not Found'))) {
        return null;
      }
      throw error;
    }
  }

  async generateAISuggestion(params: GenerateAISuggestionRequest): Promise<AISuggestionResponse> {
    const { aiUrl } = await this.getAIUrls();
    const { prompt, data_id, model = 'gemini-2.5-flash' } = params;

    const requestBody: AISuggestionRequest = {
      prompt,
      data_id,
      model,
    };

    const response = await this.fetchApi.fetch(
      `${aiUrl}v1/error_suggestions/generate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(requestBody),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to generate AI suggestion: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }
}
